import { enqueueSnackbar } from 'notistack';
import { useState, Activity, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  IconEye,
  IconPlus,
  IconTrash,
  IconSearch,
  IconPencil,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import Input from '@/components/Input/Input';
import CustomTable from '@/components/Table';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import { deletePolicy, fetchPolicies } from '@/service/editorialService';
import { IPolicyData } from '@/types';
import { IDeletePolicyPayload } from '@/types/payload';
import { formatUnixToLongDate } from '@/utils';

const PolicyManagement = () => {
  const { formatText } = useFormatText();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search?.trim(), 400);
  const [policies, setPolicies] = useState<IPolicyData[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedPolicy, setSelectedPolicy] = useState<IPolicyData | null>(
    null
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchPoliciesData = async () => {
    setLoading(true);

    try {
      const queryParams = {
        search: debouncedSearch || undefined,
      };

      const response = await fetchPolicies(queryParams);

      if (response?.status) {
        setPolicies(response?.data || []);
      } else {
        setPolicies([]);
      }
    } catch (error) {
      console.error('Error fetching policies:', error);
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPoliciesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleAddPolicy = () => {
    navigate('/editorial-management/policy/add');
  };

  const handleViewPolicy = (policy: IPolicyData) => {
    navigate(`/editorial-management/policy/view/${policy.id}`, {
      state: { policyName: policy.policy_name },
    });
  };

  const handleEditPolicy = (policy: IPolicyData) => {
    navigate(`/editorial-management/policy/edit/${policy.id}`, {
      state: { policyName: policy.policy_name },
    });
  };

  const handleDeletePolicy = (policy: IPolicyData) => {
    setSelectedPolicy(policy);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedPolicy) return;
    setDeleteLoading(true);

    try {
      const payload: IDeletePolicyPayload = {
        policy_id: selectedPolicy.id,
      };
      const response = await deletePolicy(payload);

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        fetchPoliciesData();
      } else {
        console.error('Error deleting policy:', response?.message);
      }
    } catch (error) {
      console.error('Error deleting policy:', error);
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalOpen(false);
      setSelectedPolicy(null);
    }
  };

  const showListUI =
    loading ||
    policies.length > 0 ||
    search.length > 0 ||
    debouncedSearch.length > 0;

  const columns = [
    {
      title: 'editorial.policy.columns.name',
      key: 'policy_name',
      render: (row: IPolicyData) => (
        <span className="font-medium text-neutral-primary">
          {row.policy_name}
        </span>
      ),
    },
    {
      title: 'editorial.policy.columns.last-modified',
      key: 'last_modify_at',
      render: (row: IPolicyData) => (
        <span className="text-neutral-secondary">
          {formatUnixToLongDate(Number(row.last_modify_at))}
        </span>
      ),
    },
    {
      title: 'common.actions.action',
      key: 'action',
      width: '100px',
      align: 'center',
      render: (row: IPolicyData) => {
        const actions = [
          {
            key: 'view',
            label: 'common.actions.view',
            icon: <IconEye size={18} />,
            onClick: () => handleViewPolicy(row),
          },
          {
            key: 'edit',
            label: 'common.actions.edit',
            icon: <IconPencil size={18} />,
            onClick: () => handleEditPolicy(row),
          },
          {
            key: 'delete',
            label: 'common.actions.remove',
            icon: <IconTrash size={18} />,
            isDanger: true,
            onClick: () => handleDeletePolicy(row),
          },
        ];

        return <ActionDropdown actions={actions} />;
      },
    },
  ];

  return (
    <>
      <div>
        {showListUI && (
          <div className="flex justify-between items-center mb-6 gap-2.5 ps-1">
            <Input
              name="search"
              placeholder={formatText('common.actions.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={{
                icon: <IconSearch size={20} className="text-neutral-500" />,
              }}
              className="max-w-77 md:min-w-77 w-full"
              inputClassName="py-[13px]"
            />

            <Button
              icon={<IconPlus size={18} />}
              label={formatText('editorial.policy.add-btn')}
              className="px-4 h-12"
              variant="primary"
              onClick={handleAddPolicy}
            />
          </div>
        )}

        {/* Table */}
        <CustomTable
          isLoading={loading}
          dataSource={policies}
          columns={columns}
          isEmpty={!showListUI}
          emptyImgSrc="/assets/svg/empty-editorial.svg"
          emptyTitle={formatText('editorial.policy.empty-title')}
          emptyDescription={formatText('editorial.policy.empty-desc')}
          emptyButtonText={formatText('editorial.policy.add-btn')}
          emptyButtonIcon={<IconPlus size={16} />}
          onEmptyBtnClick={handleAddPolicy}
          hidePagination
          isEmptySearch={
            policies.length === 0 &&
            (search.length > 0 || debouncedSearch.length > 0)
          }
          rootClassName="h-auto!"
          onRowClick={(id) => {
            const policy = policies.find((item) => item.id === id);
            if (policy) handleViewPolicy(policy);
          }}
        />
      </div>

      <Activity mode={isDeleteModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isDeleteModalOpen}
          setIsOpen={setIsDeleteModalOpen}
          title="editorial.policy.actions.delete-policy-title"
          descriptionPrefix="editorial.policy.actions.delete-policy-prefix"
          descriptionSuffix="editorial.policy.actions.delete-policy-suffix"
          entityName={selectedPolicy?.policy_name}
          confirmLabel={formatText(
            'editorial.policy.actions.delete-policy-button'
          )}
          onConfirm={handleConfirmDelete}
          isLoading={deleteLoading}
          isDestructive
        />
      </Activity>
    </>
  );
};

export default PolicyManagement;
